import { useState } from "react";
import { Mail, Lock, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const LoginForm = ({ onLogin, onSwitchToRegister, onSwitchToForgot }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await onLogin(email, password);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <h2 className="text-3xl font-black mb-2" style={{ color: "var(--color-text)", fontFamily: "Cabinet Grotesk, sans-serif" }}>
        Welcome Back
      </h2>
      <p className="mb-6" style={{ color: "var(--color-text-muted)" }}>Sign in to your account</p>

      {error && <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm" data-testid="login-error">{error}</div>}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: "var(--color-text-muted)" }}>Email</label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: "var(--color-text-dim)" }} />
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
              className="pl-10 rounded-lg" style={{ backgroundColor: "var(--color-input-bg)", borderColor: "var(--color-border)", color: "var(--color-text)" }}
              data-testid="auth-email-input" required />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: "var(--color-text-muted)" }}>Password</label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: "var(--color-text-dim)" }} />
            <Input type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••" className="pl-10 pr-10 rounded-lg"
              style={{ backgroundColor: "var(--color-input-bg)", borderColor: "var(--color-border)", color: "var(--color-text)" }}
              data-testid="auth-password-input" required />
            <button type="button" onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
              style={{ color: "var(--color-text-dim)" }} data-testid="toggle-password">
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>
        <Button type="submit" disabled={loading} className="w-full rounded-lg py-3 font-bold text-white"
          style={{ backgroundColor: "var(--color-accent)" }} data-testid="auth-submit-button">
          {loading ? "Signing in..." : "Sign In"}
        </Button>
      </form>

      <div className="mt-4 text-center">
        <button onClick={onSwitchToForgot} className="text-sm font-medium" style={{ color: "var(--color-accent)" }} data-testid="forgot-password-link">
          Forgot your password?
        </button>
      </div>
      <div className="mt-4 text-center">
        <button onClick={onSwitchToRegister} className="text-sm font-medium" style={{ color: "var(--color-accent)" }} data-testid="auth-toggle-button">
          Don't have an account? Sign up
        </button>
      </div>
    </>
  );
};

export default LoginForm;
